import { useMemo } from 'react';
import useSWR from 'swr';
import { orgApi } from '../utils/apiV2';
import {
  localeForCountry,
  normalizeCountryCode,
  formatMoneyForLocale,
  formatLongDateForLocale,
  tzAbbr,
} from '../utils/locale';

/**
 * useOperatorLocale — the single hook every operator surface should use to know
 * "what country/template am I rendering for?".
 *
 * Source of truth: the organization record (`org.country`), fetched once and
 * SWR-cached under a shared key so it dedupes across tabs. We seed the initial
 * value synchronously from localStorage (`orgCountry`, set at login) so there's
 * NO currency flash on first paint, then reconcile to the authoritative org
 * record in the background.
 *
 * Returns a ready-to-use locale descriptor plus a bound `formatMoney(amount)`
 * so components never hard-code "$" / "AED" / decimals again.
 *
 * @example
 *   const { formatMoney, currencyCode, isUAE, taxLabel } = useOperatorLocale();
 *   <span>{formatMoney(199.5)}</span>   // "$199.50" (US) or "AED 199.50" (UAE)
 */
export function useOperatorLocale() {
  // Authoritative source — cached & shared across all tabs.
  const { data: org, isLoading } = useSWR(
    'operator-organization',
    () => orgApi.getOrganization().then((res) => res.data),
    { revalidateOnFocus: false, dedupingInterval: 60000 }
  );

  const countryRaw =
    org?.country ||
    (typeof window !== 'undefined' ? localStorage.getItem('orgCountry') : null);

  return useMemo(() => {
    const countryCode = normalizeCountryCode(countryRaw);
    const loc = localeForCountry(countryCode);
    return {
      ...loc,
      countryCode,
      // Convenience flags
      isUSA: countryCode === 'US',
      isUAE: countryCode === 'AE',
      // Bound money formatter — the one components should call
      formatMoney: (amount) => formatMoneyForLocale(amount, loc),
      // Bound long-date formatter — "June 24, 2026" (US) / "Wed, 24 Jun 2026" (UAE)
      formatLongDate: (date) => formatLongDateForLocale(date, { ...loc, countryCode }),
      // Timezone display abbreviation (e.g. "CST", "GST")
      tzAbbr: (tz) => tzAbbr(tz || loc.defaultTimezone),
      // Messaging channel for this country (sms vs whatsapp)
      messagingChannel: loc.preferredChannel,
      isLoading: isLoading && !countryRaw,
    };
  }, [countryRaw, isLoading]);
}

export default useOperatorLocale;
